/** @jsxRuntime automatic */
/** @jsxImportSource preact */
// Script panel (SPEC §15.1, REVIEW-2026-10-01 one-screen script): one opener at a time with a small A/B selector, the hook,
// the question, what we do, the ask, gatekeeper lines, objection cards (click, or O then 1-9, 0, -, = for the first 12),
// live voicemail card (L), and three "never say" rules pinned at the bottom (the rest live in the guide and playbook).
// The jsx pragmas above let Vitest render it without a DOM (tests/fd-drawer-render.test.ts); Vite ignores them.
// No hooks in here: the opener choice lives in the dialer store, so tests can call ScriptPanel as a plain function.
import type { Campaign, QueueItem } from '../../../src/shared/api';
import { leadgenFieldsOf } from '../../../src/shared/api-core';
import type { DrawerData, LiveCheck } from '../../../src/shared/api-leadgen';
import { ErrorBanner } from '../components/ui';
import { objectionKey, OBJECTION_KEYS } from './hotkeys';
import { OBJECTION_LABELS } from './labels';
import { observationHint } from './drawer/view';
import { observationLine } from './links';
import { fillSegments, onScreenRules, openerLetter, pickLiveVoicemail, placeholdersIn, shownOpener, type TemplateVars } from './script';

export function Filled(props: { text: string; vars: TemplateVars }) {
  return (
    <>
      {fillSegments(props.text, props.vars).map((s) => (s.missing
        ? <mark class="vd-missing" title={s.missing === 'observation' ? 'No verified observation for this business' : `Unknown: ${s.missing.replace(/_/g, ' ')}`}>{s.text}</mark>
        : s.text))}
    </>
  );
}

function wordsToSeconds(text: string): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.round(words / 2.5); // ~150 words per minute
}

export function LiveVoicemailCard(props: { campaign: Campaign; item: QueueItem; vars: TemplateVars; onClose: () => void }) {
  const { item } = props;
  const full = item.membership.vmCount >= item.campaign.maxVms;
  const vm = pickLiveVoicemail(props.campaign.script?.voicemailsLive ?? [], item.membership.vmCount);
  return (
    <div class="vd-livevm card">
      <div class="row" style="justify-content:space-between">
        <div class="eyebrow">Live voicemail · {item.membership.vmCount === 0 ? 'first (VM1)' : `follow-up (VM${item.membership.vmCount + 1})`}</div>
        <button class="btn btn-sm" onClick={props.onClose}>Close <kbd>Esc</kbd></button>
      </div>
      {full ? (
        <p class="vd-bad" style="margin:6px 0 0"><b>Voicemail limit reached ({item.membership.vmCount}/{item.campaign.maxVms}).</b> Hang up without leaving a message and log "No answer".</p>
      ) : vm ? (
        <>
          <p class="vd-script-text" style="font-size:16px"><Filled text={vm.text} vars={props.vars} /></p>
          <div class="muted" style="font-size:12px">{vm.label} · about {wordsToSeconds(vm.text)} s · read it in your own words, slowly. The outcome defaults to "Left voicemail (live)".</div>
        </>
      ) : <p class="muted">This campaign has no live voicemail script yet.</p>}
    </div>
  );
}

export function ScriptPanel(props: {
  campaign: Campaign | null; campaignError: string | null; item: QueueItem; vars: TemplateVars;
  objection: number | null; onObjection: (i: number | null) => void; chordActive: boolean;
  /** The Business drawer's snapshot and live check for this lead, when loaded: says where to verify an observation. */
  drawer?: { data: DrawerData | null; live: LiveCheck | null } | null;
  /** Opener A/B: the label the setter picked for this lead (null = the rotation's pick), the picker, and whether the
   *  opener is locked because the call was dialled with it (its label went to /api/calls/prepare). */
  openerLabel?: string | null;
  onOpener?: (label: string) => void;
  openerLocked?: boolean;
}) {
  const { campaign } = props;
  if (!campaign) {
    return props.campaignError ? <ErrorBanner error={props.campaignError} /> : <p class="muted">Loading the script…</p>;
  }
  const s = campaign.script;
  if (!s || !s.openers) return <p class="muted">This campaign has no script yet. Tell PJ.</p>;
  const hasObs = !!props.vars.observation;
  const usesObservation = s.openers.some((o) => placeholdersIn(o.text).includes('observation'));
  const shown = shownOpener(s.openers, { hasObservation: hasObs, membershipId: props.item.membership?.id ?? 0, chosen: props.openerLabel ?? null });
  const selected = props.objection !== null ? s.objections?.[props.objection] : undefined;
  const rules = onScreenRules(s.complianceNotes);
  const oneQuestion = (s.questions?.length ?? 0) === 1;
  const current = shown.opener?.label ?? '';

  return (
    <div class="vd-script">
      <div class="vd-script-scroll">
        <section>
          <div class="row vd-opener-head">
            <span class="eyebrow">Opener</span>
            {shown.choices.length > 1 ? (
              <span class="vd-opener-pick" role="group" aria-label="Opener variant">
                {shown.choices.map((o, i) => (
                  <button class={`btn btn-sm ${current === o.label ? 'btn-ink' : ''}`} aria-pressed={current === o.label}
                    disabled={!!props.openerLocked || !props.onOpener}
                    title={props.openerLocked ? `This call was dialled with "${current}"` : o.label}
                    onClick={(e) => { props.onOpener?.(o.label); (e.currentTarget as HTMLElement).blur(); }}>{openerLetter(i)}</button>
                ))}
              </span>
            ) : null}
          </div>
          {!hasObs && usesObservation ? (
            <p class="vd-field-hint" style="margin:2px 0 6px">
              {observationHint({ researchAt: leadgenFieldsOf(props.item.business).researchAt, data: props.drawer?.data ?? null, live: props.drawer?.live ?? null,
                cardCanVerify: !!observationLine(props.item.business, { researched: !!leadgenFieldsOf(props.item.business).researchAt || !!props.drawer?.data?.researched })?.canUse })}
            </p>
          ) : null}
          {shown.allNeedObservation ? <p class="vd-bad" style="margin:2px 0">Every opener in this campaign needs an observation. Open with your name, Valistix, and the reason for the call.</p> : null}
          {shown.opener ? (
            <div class="vd-script-block">
              <span class="vd-script-label">{shown.opener.label}{shown.how === 'rotation' ? ' · A/B pick for this lead' : ''}</span>
              <p class="vd-script-text"><Filled text={shown.opener.text} vars={props.vars} /></p>
            </div>
          ) : null}
        </section>

        {s.reason ? <section><div class="eyebrow">Hook</div><p class="vd-script-text"><Filled text={s.reason} vars={props.vars} /></p></section> : null}

        {s.questions?.length ? (
          <section>
            <div class="eyebrow">{oneQuestion ? 'Question (then stop talking)' : 'Questions'}</div>
            {oneQuestion
              ? <p class="vd-script-text"><Filled text={s.questions[0].text} vars={props.vars} /></p>
              : <ol class="vd-script-list">{s.questions.map((q) => <li><span class="vd-script-label">{q.label}</span> <Filled text={q.text} vars={props.vars} /></li>)}</ol>}
          </section>
        ) : null}

        {s.pitch ? <section><div class="eyebrow">What we do</div><p class="vd-script-text"><Filled text={s.pitch} vars={props.vars} /></p></section> : null}
        {s.ask ? <section><div class="eyebrow">Ask for the meeting</div><p class="vd-script-text vd-ask"><Filled text={s.ask} vars={props.vars} /></p></section> : null}

        {s.gatekeeper?.length ? (
          <section>
            <div class="eyebrow">Gatekeeper</div>
            {s.gatekeeper.map((g) => <div class="vd-script-block"><span class="vd-script-label">{g.label}</span><p class="vd-script-text"><Filled text={g.text} vars={props.vars} /></p></div>)}
          </section>
        ) : null}

        {s.objections?.length ? (
          <section>
            <div class="eyebrow">Objections {props.chordActive ? <span class="chip chip-info">press its key</span> : <span class="muted">(O then the card's key{s.objections.length > OBJECTION_KEYS.length ? `; cards after ${OBJECTION_KEYS.length} are click-only` : ''})</span>}</div>
            <div class="vd-objections">
              {s.objections.map((o, i) => {
                const k = objectionKey(i);
                return (
                  <button class={`btn btn-sm ${props.objection === i ? 'btn-ink' : ''}`}
                    title={`${OBJECTION_LABELS[o.category]}${k ? ` (O then ${k})` : ' (click only: no key)'}`}
                    onClick={(e) => { props.onObjection(props.objection === i ? null : i); (e.currentTarget as HTMLElement).blur(); }}>
                    {k ? <kbd>{k}</kbd> : null} {o.label}
                  </button>
                );
              })}
            </div>
            {selected ? (
              <div class="vd-objection-answer">
                <p class="vd-script-text"><Filled text={selected.response} vars={props.vars} /></p>
                {selected.followUpQuestion ? <p class="vd-script-text"><b>Then:</b> <Filled text={selected.followUpQuestion} vars={props.vars} /></p> : null}
              </div>
            ) : null}
          </section>
        ) : null}
      </div>

      {rules.length ? (
        <div class="vd-compliance" role="note" aria-label="Compliance rules for this campaign">
          <div class="eyebrow">Never say or do</div>
          <ul>{rules.map((n) => <li>{n}</li>)}</ul>
        </div>
      ) : null}
    </div>
  );
}
